import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCdnUrl } from "../../hooks/useCdnUrl";

import { useGoogleAnalytics } from "../../hooks/useGoogleAnalytics";
import { useProductErrorsAndWarnings } from "../../hooks/useProductErrorsAndWarnings";

import { BadgeType, FloatingMenuButton } from "./floating-menu-button";
import { FloatingMenuContent } from "./floating-menu-content";

import "./floating-menu.css";

export function FloatingMenu() {
	const googleAnalytics = useGoogleAnalytics();
	const { productErrorsAndWarnings } = useProductErrorsAndWarnings();

	const [isOpen, setIsOpen] = useState(false);

	const menuRef = useRef<HTMLDivElement>(null);

	const [isCdnUrlActive] = useCdnUrl();

	const onClick = useCallback(() => {
		if (isOpen) {
			googleAnalytics.trackEventToGa({ trackingAction: "MenuClosed" });
		} else {
			googleAnalytics.trackEventToGa({ trackingAction: "MenuClicked" });
		}
		setIsOpen(!isOpen);
	}, [googleAnalytics, isOpen]);

	const onClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const touchstartHandler = useCallback(
		(event: TouchEvent) => {
			if (!menuRef.current) return;

			if (!event.composedPath().includes(menuRef.current)) {
				onClose();
			}
		},
		[onClose],
	);

	useEffect(() => {
		document.addEventListener("touchstart", touchstartHandler, true);

		return () => {
			document.removeEventListener("touchstart", touchstartHandler, true);
		};
	}, [touchstartHandler]);

	const getBadgeType = () => {
		if (!productErrorsAndWarnings.length) {
			return null;
		}

		if (productErrorsAndWarnings.some((err) => err.isError)) {
			return BadgeType.error;
		}

		return BadgeType.warning;
	};

	const cssClasses = classNames("floating-menu", {
		"floating-menu--feature-warning": !!isCdnUrlActive,
	});

	return (
		<div className={cssClasses} ref={menuRef}>
			<FloatingMenuButton onClick={onClick} badgeType={getBadgeType()} />

			{isOpen && <FloatingMenuContent onClose={onClose} />}
		</div>
	);
}
